import React from 'react';

// Tiny dependency-free Markdown renderer for assistant answers. Deliberately
// not a full CommonMark parser — it covers what the LLM actually emits:
// headings, bold/italic, inline code, links, ordered/unordered lists (with one
// level of nesting), blockquotes, fenced code, GFM pipe tables, and rules.
// Chosen over react-markdown because that stack is ESM-only and fights CRA's
// webpack/jest; this renders the same content with zero install risk.

const md = {
    root: { display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 },
    p: { margin: 0, lineHeight: 1.7 },
    h1: { margin: '4px 0 0', fontSize: '20px', fontWeight: 700, lineHeight: 1.3 },
    h2: { margin: '4px 0 0', fontSize: '17px', fontWeight: 700, lineHeight: 1.3 },
    h3: { margin: '2px 0 0', fontSize: '15px', fontWeight: 700, lineHeight: 1.3 },
    ul: { margin: 0, paddingLeft: '22px', display: 'flex', flexDirection: 'column', gap: '4px' },
    ol: { margin: 0, paddingLeft: '22px', display: 'flex', flexDirection: 'column', gap: '4px' },
    li: { lineHeight: 1.6 },
    code: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.88em', background: 'var(--surface, #f1f5f9)', padding: '1px 5px', borderRadius: '5px' },
    pre: { margin: 0, background: 'var(--surface, #f1f5f9)', padding: '12px 14px', borderRadius: '10px', overflowX: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12.5px', lineHeight: 1.5 },
    link: { color: 'var(--accent)', textDecoration: 'underline' },
    quote: { margin: 0, padding: '2px 0 2px 12px', borderLeft: '3px solid var(--border)', color: 'var(--text-secondary)' },
    hr: { border: 0, borderTop: '1px solid var(--border)', margin: '4px 0' },
    tableWrap: { overflowX: 'auto', minWidth: 0, border: '1px solid var(--border)', borderRadius: '10px' },
    table: { borderCollapse: 'collapse', fontSize: '13px', width: 'max-content', minWidth: '100%' },
    th: { textAlign: 'left', padding: '7px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', fontWeight: 700, whiteSpace: 'nowrap', background: '#f8fafc' },
    td: { padding: '7px 12px', borderBottom: '1px solid #f1f5f9', color: 'var(--text-primary)', verticalAlign: 'top', whiteSpace: 'nowrap' },
};

let uid = 0;
const nk = () => `m${uid++}`;

// ---- inline: **bold**, *italic*/_italic_, `code`, [text](url) ----
const INLINE = /(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*\s][^*]*\*)|(_[^_\s][^_]*_)/;

const WORD = /[A-Za-z0-9]/;

function renderInline(text) {
    const out = [];
    let rest = String(text);
    while (rest) {
        const m = INLINE.exec(rest);
        if (!m) { out.push(rest); break; }
        const t = m[0];
        // CommonMark intraword rule: `_` only opens/closes emphasis at a word
        // boundary. Underscores glued to alphanumerics on either side (e.g.
        // Core_Google_Search) are literal — not italic. (`*` is allowed
        // intraword, so it's exempt.) Emit the opener literally and rescan.
        if (t[0] === '_') {
            const before = m.index > 0 ? rest[m.index - 1] : '';
            const after = rest[m.index + t.length] || '';
            if (WORD.test(before) || WORD.test(after)) {
                const openLen = t.startsWith('__') ? 2 : 1;
                out.push(rest.slice(0, m.index + openLen));
                rest = rest.slice(m.index + openLen);
                continue;
            }
        }
        if (m.index > 0) out.push(rest.slice(0, m.index));
        if (t[0] === '`') {
            out.push(<code key={nk()} style={md.code}>{t.slice(1, -1)}</code>);
        } else if (t[0] === '[') {
            const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(t);
            out.push(<a key={nk()} href={lm[2]} target="_blank" rel="noreferrer" style={md.link}>{lm[1]}</a>);
        } else if (t.startsWith('**') || t.startsWith('__')) {
            out.push(<strong key={nk()}>{renderInline(t.slice(2, -2))}</strong>);
        } else {
            out.push(<em key={nk()}>{renderInline(t.slice(1, -1))}</em>);
        }
        rest = rest.slice(m.index + t.length);
    }
    return out;
}

const listItem = (line) => {
    const m = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (!m) return null;
    return { indent: m[1].replace(/\t/g, '  ').length, ordered: /\d/.test(m[2]), content: m[3] };
};

const isSpecial = (line, next) =>
    /^\s*(```|~~~)/.test(line) ||
    /^#{1,6}\s/.test(line) ||
    /^\s*([-*+]|\d+[.)])\s+/.test(line) ||
    /^\s*>\s?/.test(line) ||
    /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line) ||
    (/\|/.test(line) && next && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*$/.test(next) && /\|/.test(next));

function parseList(lines, start) {
    const first = listItem(lines[start]);
    const items = [];
    let i = start;
    while (i < lines.length) {
        const lm = listItem(lines[i]);
        if (!lm || lm.indent !== first.indent) break;
        const content = [lm.content];
        const children = [];
        i++;
        while (i < lines.length) {
            if (lines[i].trim() === '') { i++; break; }
            const nlm = listItem(lines[i]);
            if (nlm && nlm.indent > first.indent) {
                const sub = parseList(lines, i);
                children.push(sub.node);
                i = sub.next;
            } else if (nlm) {
                break; // sibling or dedent
            } else if (/^\s+\S/.test(lines[i])) {
                content.push(lines[i].trim()); i++; // continuation line
            } else {
                break;
            }
        }
        items.push(<li key={nk()} style={md.li}>{renderInline(content.join(' '))}{children}</li>);
    }
    const Tag = first.ordered ? 'ol' : 'ul';
    return { node: <Tag key={nk()} style={first.ordered ? md.ol : md.ul}>{items}</Tag>, next: i };
}

function parseTable(lines, start) {
    const cells = (l) => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
    const headers = cells(lines[start]);
    let i = start + 2;
    const rows = [];
    while (i < lines.length && lines[i].trim() !== '' && /\|/.test(lines[i])) { rows.push(cells(lines[i])); i++; }
    const node = (
        <div key={nk()} style={md.tableWrap}>
            <table style={md.table}>
                <thead><tr>{headers.map((h, j) => <th key={j} style={md.th}>{renderInline(h)}</th>)}</tr></thead>
                <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} style={md.td}>{renderInline(c)}</td>)}</tr>)}</tbody>
            </table>
        </div>
    );
    return { node, next: i };
}

function parseBlocks(text) {
    const lines = String(text).replace(/\r\n/g, '\n').split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (line.trim() === '') { i++; continue; }

        const fence = /^\s*(```|~~~)/.exec(line);
        if (fence) {
            const body = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith(fence[1])) { body.push(lines[i]); i++; }
            i++;
            out.push(<pre key={nk()} style={md.pre}><code>{body.join('\n')}</code></pre>);
            continue;
        }
        if (/\|/.test(line) && i + 1 < lines.length && /\|/.test(lines[i + 1]) && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*$/.test(lines[i + 1])) {
            const t = parseTable(lines, i); out.push(t.node); i = t.next; continue;
        }
        const h = /^(#{1,6})\s+(.*)$/.exec(line);
        if (h) {
            const lvl = Math.min(h[1].length, 3);
            out.push(React.createElement(`h${lvl}`, { key: nk(), style: md[`h${lvl}`] }, renderInline(h[2])));
            i++; continue;
        }
        if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { out.push(<hr key={nk()} style={md.hr} />); i++; continue; }
        if (/^\s*>\s?/.test(line)) {
            const body = [];
            while (i < lines.length && /^\s*>\s?/.test(lines[i])) { body.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
            out.push(<blockquote key={nk()} style={md.quote}>{parseBlocks(body.join('\n'))}</blockquote>);
            continue;
        }
        if (listItem(line)) { const l = parseList(lines, i); out.push(l.node); i = l.next; continue; }

        const para = [];
        while (i < lines.length && lines[i].trim() !== '' && !isSpecial(lines[i], lines[i + 1])) { para.push(lines[i]); i++; }
        out.push(
            <p key={nk()} style={md.p}>
                {para.map((ln, idx) => <React.Fragment key={idx}>{idx > 0 && <br />}{renderInline(ln)}</React.Fragment>)}
            </p>,
        );
    }
    return out;
}

export default function Markdown({ text }) {
    if (!text) return null;
    return <div style={md.root}>{parseBlocks(text)}</div>;
}
